import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { processArticleReferences } from '../../../utils/articleReferenceUtils';

interface SectionSummaryProps {
  summary: string | { summary: string | object } | object;
  onArticleReferences?: (articleIds: string[]) => void;
}

export const SectionSummary: React.FC<SectionSummaryProps> = ({ summary, onArticleReferences }) => {
  const getSummaryText = () => {
    
    let rawText = '';
    
    // Handle string summary
    if (typeof summary === 'string') {
      rawText = summary;
    } 
    // Handle object summary
    else if (typeof summary === 'object' && summary) {
      // Try direct summary field
      if ('summary' in summary && typeof summary.summary === 'string') {
        rawText = summary.summary;
      } 
      // Try nested summary field (for deeply nested structures)
      else if ('summary' in summary && summary.summary && typeof summary.summary === 'object' && 'summary' in summary.summary) {
        rawText = String(summary.summary.summary);
      }
      // Try converting non-string summary to string
      else if ('summary' in summary && summary.summary) {
        rawText = String(summary.summary);
      }
      // Check if the object itself might be a parsed JSON with the text directly in it
      else if ('category_name' in summary && 'summary' in summary) {
        // This looks like a parsed category profile
        rawText = String(summary.summary);
      }
      // If we have an object but no clear summary field, try to stringify
      else {
        try {
          // Check if it's an empty object
          if (Object.keys(summary).length === 0) {
            return 'No summary available';
          }
          
          // Look for any field that might contain text with article references
          const possibleTextFields = ['text', 'content', 'description', 'summary'];
          for (const field of possibleTextFields) {
            if (field in summary && typeof summary[field] === 'string' && summary[field].includes('Article ')) {
              rawText = summary[field];
              break;
            }
          }
          
          // If no text field found, stringify the object
          if (!rawText) {
            rawText = JSON.stringify(summary, null, 2);
          }
        } catch (e) {
          console.error('Error processing summary object:', e);
          return 'Error processing summary';
        }
      }
    } 
    // Handle null/undefined
    else {
      return 'No summary available';
    }
    
    // Always process article references if we have text
    if (rawText) {
      const { processedText, referenceMapping } = processArticleReferences(rawText);
      
      // Notify parent component about the article IDs found if callback is provided
      if (onArticleReferences && referenceMapping.orderedIds.length > 0) {
        onArticleReferences(referenceMapping.orderedIds);
      }
      
      return processedText;
    }
    
    return rawText;
  };

  return (
    <div className="bg-[#1A1A1A] rounded-lg p-4 border border-gray-500">
      <h4 className="text-sm font-semibold text-[#CECFD2] mb-3">Summary</h4>
      <div className="text-sm">
        <div className="prose prose-sm prose-invert max-w-none [&>p]:text-[#CECFD2] [&>ul]:text-[#CECFD2] [&>ol]:text-[#CECFD2]">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {getSummaryText()}
          </ReactMarkdown>
        </div>
      </div>
    </div>
  );
};